import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from "react-router";
import { Sentry } from "./sentry";

import type { Route } from "./+types/root";
import "./app.css";
import { Layout as AppLayout } from "./components/Layout";
import { ToastProvider } from "./components/Toast";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600;8..60,700&family=Instrument+Sans:wght@400;500;600;700&display=swap",
  },
];

// App component movido al final

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "¡Ups!";
  let details = "Ha ocurrido un error inesperado.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "La página solicitada no pudo ser encontrada."
        : error.statusText || details;
  } else if (error instanceof Error) {
    Sentry.captureException(error);
    if (import.meta.env.DEV) {
      details = error.message;
      stack = error.stack;
    }
  } else {
    Sentry.captureException(error);
  }

  return (
    <main className="error-boundary">
      <div className="error-card">
        <h1>{message}</h1>
        <p>{details}</p>
        {stack && (
          <pre>
            <code>{stack}</code>
          </pre>
        )}
      </div>
      <style>{`
        .error-boundary {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 80vh;
          padding: 2rem;
        }
        .error-card {
          background: white;
          padding: 3rem;
          border-radius: 1rem;
          box-shadow: var(--shadow-ambient);
          max-width: 600px;
          text-align: center;
        }
        h1 { font-family: var(--font-headline); font-size: 4rem; color: var(--primary); margin-bottom: 1rem; }
        p { color: var(--on-surface-variant); margin-bottom: 2rem; }
        pre { background: var(--surface-low); padding: 1rem; border-radius: 0.5rem; text-align: left; overflow-x: auto; font-size: 0.8rem; }
      `}</style>
    </main>
  );
}

// Separate Layout component for root wrapper (Head/Body)
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const { pathname } = useLocation();
  
  // Lista de rutas que NO deben llevar el Layout del Dashboard
  const isPublicRoute =
    pathname === "/login" ||
    pathname === "/register" ||
    pathname === "/catalogo" ||
    pathname === "/social-callback" ||
    pathname.startsWith("/p/") ||
    pathname.startsWith("/a/");

  if (isPublicRoute) {
    return (
      <ToastProvider>
        <Outlet />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </ToastProvider>
  );
}
